<script setup lang="ts">
import { Editor } from "@tiptap/vue-3";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import { ref } from "vue";

export interface IActions {
  name: string;
  icon: string;
  label: string;
  function: Function;
  visible: boolean;
  shortcut?: string;
}

const props = defineProps({
  action: {
    type: Object as PropType<IActions>,
  },
  editor: Editor,
});

const fileInput = ref<HTMLInputElement | null>(null);

function triggerUpload() {
  fileInput.value?.click();
}

function onFileChange(event: Event) {
  const target = event.target as HTMLInputElement;
  if (target.files && target.files.length > 0) {
    loadImage(target.files[0]);
  }

  target.value = "";
}

function loadImage(file: File) {
  const reader = new FileReader();
  reader.onload = (event) => {
    const imageUrl = event.target?.result;
    if (imageUrl) {
      props.action?.function(imageUrl);
    }
  };
  reader.readAsDataURL(file);
}
</script>
<template>
  <div v-if="action" class="flex gap-1">
    <div class="button-container" :class="{ 'is-active': editor?.isActive(action.name) }" @click="triggerUpload">
      <SvgIcon class="svg-default-icon" :class="{ 'is-active': editor?.isActive(action.name) }" :name="action.icon" />
    </div>
    <input ref="fileInput" type="file" style="display: none" @change="onFileChange" />
  </div>
</template>
<style scoped lang="scss">
.button-container {
  @apply flex flex-row justify-center items-center rounded-[8px] w-fit;
  height: 36px;
  min-width: 36px;
  cursor: pointer;
  border: 1px solid var(--grey-lightest);
  color: var(--grey-lightest);
  transition: border-color 0.3s;

  &:hover,
  &:hover * {
    background-color: var(--grey-light);
    border-color: var(--grey-medium-light);
  }

  &:active,
  &:active * {
    border-color: var(--blue-deep-dark);
    color: var(--blue-deep-dark);
  }

  &.is-active {
    border-color: var(--primary-blue-color);
    color: var(--primary-blue-color);
  }

  &.is-active:hover,
  &.is-active:hover * {
    border-color: var(--blue-deep-dark);
    color: var(--blue-deep-dark);
    background-color: var(--grey-light);
  }

  input[type="file"] {
    display: none;
  }
}

.svg-default-icon {
  @apply w-[16px] h-[16px] text-secondary-color;

  &:active {
    color: var(--blue-deep-dark);
  }

  &.is-active {
    color: var(--primary-blue-color);
  }
}
</style>
